import { ApiService } from '@b2b/common/services/api/types';
import angular, { IFilterService, IQService } from 'angular';

type ValidateService = any; // TODO: This is temporary

const COUPON_CODE_STATUS = {
  VALID_COUPON_CODE: 1,
  INVALID_COUPON_CODE: 0,
  CONFLICTING_COUPON_CODE: -1,
} as const;

export class CartCouponsService {
  static $inject = ['$q', '$filter', 'ApiService', 'COUPON_CODE_STATUS', 'ValidateService'];
  parent = {} as {
    model: { id: string; couponCodes: string[] };
    updateCart: () => Promise<any>;
  };

  model = [] as { code: string; valid?: number }[];

  valid = false;
  validCouponCodes: any[] = [];
  invalidCouponCodes: any[] = [];
  conflictingCouponCodes: any[] = [];
  events = {};
  errors = false as Record<string, any> | false;

  constructor(
    private $q: IQService,
    private $filter: IFilterService,
    private ApiService: ApiService,
    public codes: typeof COUPON_CODE_STATUS,
    private ValidateService: ValidateService
  ) {}

  init(parent) {
    this.parent = parent;
    return this;
  }

  setModel(data) {
    this.model = data;
  }

  getModel() {
    return this.model;
  }

  resetModel() {
    this.model = [];
    this.validCouponCodes = [];
    this.invalidCouponCodes = [];
    this.conflictingCouponCodes = [];
  }

  getModelCodes() {
    const result: string[] = [];

    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i] && this.model[i].code) {
        result.push(this.model[i].code);
      }
    }

    return result;
  }

  update() {
    this.parent.model.couponCodes = this.getModelCodes();
    return this.parent.updateCart();
  }

  findValid(code) {
    return code ? this.$filter('filter')(this.validCouponCodes, { couponCode: code }) : [];
  }

  isValid(code) {
    return this.findValid(code).length || false;
  }

  isInvalid(code) {
    return this.invalidCouponCodes.indexOf(code) > -1;
  }

  isConflicting(code) {
    return this.conflictingCouponCodes.indexOf(code) > -1;
  }

  checkCodes() {
    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.isInvalid(this.model[i].code)) {
        this.model[i].valid = this.codes.INVALID_COUPON_CODE;
      } else if (this.isConflicting(this.model[i].code)) {
        this.model[i].valid = this.codes.CONFLICTING_COUPON_CODE;
      } else if (this.isValid(this.model[i].code)) {
        this.model[i].valid = this.codes.VALID_COUPON_CODE;
      }
    }
  }

  findMembershipCoupon(membershipInfo) {
    return this.$filter('filter')(this.validCouponCodes, (v1, i1, a1) => {
      const result =
        this.$filter('filter')(v1.membershipOfferingInfo, {
          levelId: membershipInfo.membershipCategoryId,
          offeringId: membershipInfo.membershipOfferingId,
        }) || [];

      return result.length ? v1 : false;
    });
  }

  /**
   * ```
   * {
   *   "valid": true,
   *   "invalidCouponCodes": [],
   *   "validCouponcodes": [
   *     {
   *       "eventPricepointMap": {
   *         "583600b922ae230c4aec47a1": ["53a879f130042d8e666ef9c6", "53a879ba30042d8e666ef9c5"]
   *       },
   *       "couponCode": "KCBUWINS",
   *       "valid": true,
   *       "events": ["583600b922ae230c4aec47a1", "583600b922ae230c4aec47a1"]
   *     }
   *   ],
   *   "conflictingCoupons": []
   * }
   * ```
   */
  findEventTicketCoupon(selectedEventId, ticketTypeId) {
    let eventId;
    const result: any[] = [];

    for (let i = 0, ii = this.validCouponCodes.length; i < ii; i++) {
      const coupon = this.validCouponCodes[i];

      if (!selectedEventId) {
        continue;
      }

      for (eventId in coupon.eventPricepointMap) {
        const eventPricepoints = coupon.eventPricepointMap[eventId] || [];

        if (eventId === selectedEventId) {
          const found = {
            eventId: selectedEventId,
            couponCode: coupon.couponCode,
          };

          // Check if the event price point map includes the correct ticket type
          if (ticketTypeId && eventPricepoints.indexOf(ticketTypeId) > -1) {
            result.push(found);
          }

          break;
        }
      }
    }

    return result;
  }

  validate() {
    const q = this.$q.defer();
    const couponCodes = this.getModelCodes();

    this.ApiService.cart
      .validateCoupons({
        shoppingCartId: this.parent.model.id,
        couponCodes: couponCodes,
      })
      .then(
        (response) => {
          this.validCouponCodes = response.data.payload.validCouponcodes || [];
          this.invalidCouponCodes = response.data.payload.invalidCouponCodes || [];
          this.conflictingCouponCodes = response.data.payload.conflictingCoupons || [];
          this.checkCodes();
          this.process(response.data.payload);
          q.resolve(response);
        },
        (response) => {
          q.reject(response);
        }
      );

    return q.promise;
  }

  validateCoupons(couponCodes) {
    let result = true;
    const validators: any[] = [];
    let couponCode;

    if (Array.isArray(couponCodes) && couponCodes.length) {
      for (let i = 0, ii = couponCodes.length; i < ii; i++) {
        couponCode = couponCodes[i];
        validators.push({
          field: 'form-coupon-' + i,
          coupon: {
            message: 'Invalid code',
            test: { code: couponCode },
          },
        });

        result = this.ValidateService.validate(validators);
      }
      if (result) {
        this.clearErrors();
      } else {
        this.errors = this.ValidateService.getErrorHash();
      }
    } else {
      this.clearErrors();
    }

    return result;
  }

  clearErrors() {
    this.errors = false;
  }

  setValid(valid) {
    this.valid = valid;
  }

  addCoupon(code) {
    const emptyCodes = this.model.filter((coupon) => {
      return coupon.code === '';
    });

    if (!emptyCodes.length) {
      this.model.push({ code: code || '' });
    }
  }

  findCoupon(code) {
    let found: { index: number; coupon: any } | false = false;

    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].code === code) {
        found = {
          index: i,
          coupon: this.model[i],
        };
      }
    }

    return found;
  }

  process(data) {
    const coupons = data.validCouponcodes || [];
    const invalidCoupons = data.invalidCouponCodes || [];
    const conflictingCoupons = data.conflictingCoupons || [];
    const valid = data.valid;
    const events = {};
    let found;

    this.setValid(valid);
    for (let i = 0, ii = coupons.length; i < ii; i++) {
      found = this.findCoupon(coupons[i].couponCode);
      if (!found) {
        found = this.addCoupon(coupons[i].couponCode);
      }
      if (found) {
        events[coupons[i].couponCode] = Array.isArray(coupons[i].events) ? coupons[i].events : [];
      }
    }
    this.events = events;

    this.validCouponCodes = coupons;

    this.invalidCouponCodes = invalidCoupons;
    for (let i = 0, ii = this.invalidCouponCodes.length; i < ii; i++) {
      found = this.findCoupon(this.invalidCouponCodes[i]);
      if (found) {
        if (!this.errors) {
          this.errors = {};
        }
        this.errors['form-coupon-' + found.index] = {
          message: 'Invalid coupon',
        };
      }
    }

    this.conflictingCouponCodes = conflictingCoupons || [];

    for (let i = 0, ii = conflictingCoupons.length; i < ii; i++) {
      for (let j = 1, jj = this.conflictingCouponCodes[i].conflictingCouponCodes.length; j < jj; j++) {
        //first one is valid, the rest are invalid
        found = this.findCoupon(this.conflictingCouponCodes[i].conflictingCouponCodes[j]);
        if (found) {
          if (!this.errors) {
            this.errors = {};
          }
          this.errors['form-coupon-' + found.index] = {
            message: 'Coupon conflicts with: ' + this.conflictingCouponCodes[i].conflictingCouponCodes[0],
          };
        }
      }
    }
  }
}

export const CartCouponsServiceModule = angular
  .module('acme.common.service.cart.coupons', ['acme.common.service.api', 'acme.common.service.validate'])
  .value('COUPON_CODE_STATUS', COUPON_CODE_STATUS)
  .service('CartCouponsService', CartCouponsService).name;
